/* SPDX-License-Identifier: AGPL-3.0-or-later */

import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ElementRef,
  Output,
  EventEmitter,
} from '@angular/core';
import { MatCard } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { SharedMediaMetadataDto } from '../../../../openapi-generated';
import { SharedMediaEndpointService } from '@api';
import { ConfirmationService } from '../../services/confirmation.service';

@Component({
  selector: 'app-party-picture-card',
  standalone: true,
  imports: [MatCard, MatIcon, MatIconButton, MatTooltip],
  templateUrl: './party-picture-card.component.html',
  styleUrls: ['./party-picture-card.component.scss'],
})
export class PartyPictureCardComponent implements OnInit, OnDestroy {
  @Input() picture: SharedMediaMetadataDto | undefined;
  imageUrl: string | undefined;
  @Output() pictureStateChanged = new EventEmitter<void>();

  private observer: IntersectionObserver | undefined;

  constructor(
    private confirmationService: ConfirmationService,
    private sharedMediaService: SharedMediaEndpointService,
    private element: ElementRef,
  ) {}

  ngOnInit(): void {
    this.observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && this.picture && this.picture.id) {
          this.loadImage(this.picture.id);
          this.observer?.unobserve(this.element.nativeElement);
        }
      });
    });

    this.observer.observe(this.element.nativeElement);
  }

  ngOnDestroy(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  loadImage(sharedMediaId: number): void {
    this.sharedMediaService.getSharedMediaImage(sharedMediaId).subscribe(
      response => {
        this.imageUrl = URL.createObjectURL(response as any); // eslint-disable-line
      },
      error => {
        // Handle errors
        console.error('Error fetching shared media image:', error);
      },
    );
  }
  async openConfirmDialog(action: 'approve' | 'reject' | 'delete'): Promise<void> {
    const confirmed = await this.confirmationService.openConfirmationDialog(`${action} picture`);
    if (confirmed) {
      switch (action) {
        case 'approve':
          this.approvePicture();
          break;
        case 'reject':
          this.rejectPicture();
          break;
        case 'delete':
          this.deletePicture();
          break;
      }
    }
  }

  approvePicture(): void {
    if (this.picture && this.picture.id) {
      this.sharedMediaService.updateState(this.picture.id, { state: 'APPROVED' }).subscribe({
        next: () => {
          console.log('Picture approved successfully.');
          this.pictureStateChanged.emit();
        },
        error: () => {
          console.error('Error approving picture');
        },
      });
    }
  }

  rejectPicture(): void {
    if (this.picture && this.picture.id) {
      this.sharedMediaService.updateState(this.picture.id, { state: 'REJECTED' }).subscribe({
        next: () => {
          console.log('Picture rejected successfully.');
          this.pictureStateChanged.emit();
        },
        error: () => {
          console.error('Error rejecting picture');
        },
      });
    }
  }

  deletePicture(): void {
    if (this.picture && this.picture.id) {
      this.sharedMediaService.deleteSharedMedia(this.picture.id).subscribe(
        () => {
          console.log('Picture deleted successfully.');
          this.pictureStateChanged.emit();
        },
        error => {
          console.error('Error deleting picture:', error);
        },
      );
    }
  }

  getBackgroundColor(): string {
    if (this.picture?.state === 'APPROVED') {
      return '#f9ffe8';
    } else if (this.picture?.state === 'REJECTED') {
      return '#ffe8e8';
    } else {
      return 'rgb(235, 235, 235)';
    }
  }
}
